import React, { useEffect, useState } from 'react';
import { message, Modal, Button, Form, Input } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useEntityData, useMutationUrn } from '../../EntityContext';
import { useAddLinkMutation, useUpdateLinkMutation } from '../../../../../graphql/mutations.generated';
import analytics, { EventType, EntityActionType } from '../../../../analytics';
import { useUserContext } from '../../../../context/useUserContext';

export type LinkType = {
    link: string;
    label: string;
};

type AddLinkProps = {
    buttonProps?: Record<string, unknown>;
    refetch?: () => Promise<any>;
    isEdit?: boolean;
    existingLinks?: LinkType[];
    defaultValue?: LinkType;
};

export const AddLinkModal = ({ buttonProps, refetch, isEdit, existingLinks, defaultValue }: AddLinkProps) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true);
    const mutationUrn = useMutationUrn();
    const user = useUserContext();
    const { entityType } = useEntityData();
    const [addLinkMutation] = useAddLinkMutation();
    const [updateLinkMutation] = useUpdateLinkMutation();

    const [form] = Form.useForm();

    useEffect(() => {
        if (defaultValue) {
            form.setFieldsValue({
                url: defaultValue.link,
                label: defaultValue.label,
            });
            setIsFormValid(true);
        }
    }, [defaultValue, form]);

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleClose = () => {
        form.resetFields();
        setIsModalVisible(false);
    };

    const handleAdd = async (formData: any) => {
        if (user?.urn) {
            if (isEdit && defaultValue) {
                try {
                    await updateLinkMutation({
                        variables: {
                            input: {
                                currentUrl: defaultValue.link,
                                newUrl: formData.url,
                                label: formData.label,
                                resourceUrn: mutationUrn,
                            },
                        },
                    });
                    message.success({ content: 'Link Updated', duration: 2 });
                    analytics.event({
                        type: EventType.EntityActionEvent,
                        entityType,
                        entityUrn: mutationUrn,
                        actionType: EntityActionType.UpdateLinks,
                    });
                } catch (e: unknown) {
                    message.destroy();
                    if (e instanceof Error) {
                        message.error({ content: `Failed to update link: \n ${e.message || ''}`, duration: 3 });
                    }
                }
            } else {
                try {
                    await addLinkMutation({
                        variables: {
                            input: { linkUrl: formData.url, label: formData.label, resourceUrn: mutationUrn },
                        },
                    });
                    message.success({ content: 'Link Added', duration: 2 });
                    analytics.event({
                        type: EventType.EntityActionEvent,
                        entityType,
                        entityUrn: mutationUrn,
                        actionType: EntityActionType.UpdateLinks,
                    });
                } catch (e: unknown) {
                    message.destroy();
                    if (e instanceof Error) {
                        message.error({ content: `Failed to add link: \n ${e.message || ''}`, duration: 3 });
                    }
                }
            }
            refetch?.();
            handleClose();
        } else {
            message.error({ content: `Error adding link: no user`, duration: 2 });
        }
    };

    return (
        <span>
            {isEdit ? (
                <Button onClick={showModal} type="text" shape="circle">
                    <EditOutlined />
                </Button>
            ) : (
                <Button
                    type="default"
                    data-testid="add-link-button"
                    icon={<PlusOutlined aria-hidden="true" />}
                    onClick={showModal}
                    {...buttonProps}
                >
                    Add Link
                </Button>
            )}

            <Modal
                title={defaultValue ? 'Edit Link' : 'Add Link'}
                visible={isModalVisible}
                destroyOnClose
                className="f-modal"
                onCancel={handleClose}
                centered
                footer={[
                    <Button type="default" onClick={handleClose}>
                        Cancel
                    </Button>,
                    <Button
                        type="primary"
                        data-testid="add-link-modal-add-button"
                        form="addLinkForm"
                        disabled={isFormValid}
                        key="submit"
                        htmlType="submit"
                    >
                        {defaultValue ? 'Update' : 'Add'}
                    </Button>,
                ]}
            >
                <Form
                    form={form}
                    name="addLinkForm"
                    onFinish={handleAdd}
                    onFieldsChange={(_, fields) => {
                        const isValueEmpty = fields.some((field) => !field.value);
                        const checkSameValue =
                            form.getFieldValue('url') === defaultValue?.link &&
                            form.getFieldValue('label') === defaultValue?.label;
                        setIsFormValid(
                            form.getFieldsError().some((field) => field.errors.length > 0) ||
                                isValueEmpty ||
                                checkSameValue,
                        );
                    }}
                    layout="vertical"
                >
                    <Form.Item
                        data-testid="add-link-modal-url"
                        name="url"
                        label="URL"
                        rules={[
                            {
                                type: 'url',
                                required: true,
                                message: 'This field must be a valid url.',
                            },
                            {
                                validator: (_, value) => {
                                    const existingLink = existingLinks?.find((link) => link.link === value);
                                    const isDuplicate = existingLink && (defaultValue?.link !== value || !isEdit);
                                    return isDuplicate
                                        ? Promise.reject(
                                              new Error(`This URL already exists with label ${existingLink?.label}`),
                                          )
                                        : Promise.resolve();
                                },
                            },
                        ]}
                        initialValue={defaultValue?.link ?? undefined}
                    >
                        <Input size="large" placeholder="https://" autoFocus />
                    </Form.Item>
                    <Form.Item
                        name="label"
                        label="Label"
                        className="mb-0"
                        data-testid="add-link-modal-label"
                        rules={[
                            {
                                required: true,
                                message: 'A label is required.',
                            },
                        ]}
                        initialValue={defaultValue?.label ?? undefined}
                    >
                        <Input size="large" placeholder="A short label for this link" />
                    </Form.Item>
                </Form>
            </Modal>
        </span>
    );
};
